import { FC, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

export interface iProjectAccordionItem {
  id: string;
  title: string;
  category: string;
  src: string;
  link: string;
  mediaType?: 'image' | 'video';
  onClick?: () => void;
}

const ProjectAccordionItem: FC<{ item: iProjectAccordionItem }> = ({ item }) => {
  const [isHovered, setIsHovered] = useState(false);

  const mediaContent = (
    <>
      {item.src.endsWith('.mp4') || item.mediaType === 'video' ? (
        <video 
          src={item.src} 
          autoPlay 
          loop 
          muted 
          playsInline 
          className="w-full h-full object-cover scale-105 group-hover:scale-100 transition-transform duration-700"
        />
      ) : (
        <img 
          src={item.src} 
          alt={item.title} 
          className="w-full h-full object-cover scale-105 group-hover:scale-100 transition-transform duration-700"
        />
      )}
      {/* Subtle overlay */}
      <div className="absolute inset-0 bg-black/10 dark:bg-black/20" />
    </>
  );

  return (
    <div 
      className="border-b border-black/20 dark:border-white/20 group cursor-pointer transition-colors hover:bg-black/[0.02] dark:hover:bg-white/[0.02]"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={item.onClick}
    >
      {/* MOBILE DESIGN: Full-Width Square Asset + Minimalist Info Row (hidden on desktop) */}
      <div className="flex flex-col md:hidden py-6 px-2">
        {/* That prominent 1:1 Square Mobile Image Asset */}
        <div className="w-full aspect-square rounded-2xl overflow-hidden bg-gray-100 dark:bg-[#111] relative mb-4 shadow-sm">
          {mediaContent}
          
          {/* Arrow overlay inside the card */}
          <div className="absolute bottom-4 right-4 sm:bottom-6 sm:right-6">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-black/20 dark:bg-white/20 backdrop-blur-md border border-white/30 dark:border-white/20 flex items-center justify-center shadow-lg">
              <ArrowRight className="w-5 h-5 text-white" />
            </div>
          </div>
        </div>
        
        {/* Minimalist Info Row (Name - What they do) */}
        <div className="flex items-center justify-between">
          <div className="flex flex-col pr-4">
            <h3 className="text-2xl sm:text-3xl font-medium tracking-tight">
              {item.title} 
              <span className="text-sm sm:text-base opacity-60 font-light ml-2">
                - {item.category}
              </span>
            </h3>
          </div>
        </div>
      </div>

      {/* DESKTOP DESIGN: Animated Accordion Row (hidden on mobile) */}
      <div className="hidden md:block md:px-0">
        <div className="flex items-center justify-between py-10">
          <div className="w-1/3">
            <h3 className="text-5xl lg:text-7xl font-light tracking-tight group-hover:translate-x-4 transition-transform duration-500">
              {item.title}
            </h3>
          </div>
          
          <div className="w-1/3 text-center">
            <span className="text-lg opacity-60">
              {item.category}
            </span>
          </div>

          <div className="w-1/3 flex justify-end">
            <div className="w-16 h-16 rounded-full border border-black/20 dark:border-white/20 flex items-center justify-center relative overflow-hidden group-hover:bg-black dark:group-hover:bg-white transition-colors duration-500">
              {/* First arrow */}
              <ArrowRight className="w-6 h-6 absolute transition-transform duration-500 group-hover:translate-x-12 text-black dark:text-white group-hover:text-white dark:group-hover:text-black" />
              {/* Second arrow */}
              <ArrowRight className="w-6 h-6 absolute -translate-x-12 transition-transform duration-500 group-hover:translate-x-0 text-white dark:text-black" />
            </div>
          </div>
        </div>

        {/* Desktop Expanding Image Element */}
        <motion.div 
          initial={false}
          animate={{ height: isHovered ? 'auto' : 0, opacity: isHovered ? 1 : 0 }}
          transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
          className="overflow-hidden"
        >
          <div className="pb-8 overflow-hidden rounded-2xl md:px-4">
            <div className="w-full h-[500px] lg:h-[600px] rounded-2xl overflow-hidden bg-gray-100 dark:bg-[#111] relative">
              {mediaContent}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export const ProjectAccordionList: FC<{ items: iProjectAccordionItem[] }> = ({ items }) => {
  return (
    <div className="w-full">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 md:mb-20 px-4 md:px-0">
        <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-normal max-w-2xl leading-tight tracking-tight">
          Good Design Informs.<br />
          Great Design Connects.
        </h2>
        <Link 
          to="/projects" 
          className="mt-8 md:mt-0 px-8 py-4 bg-black dark:bg-white text-white dark:text-black rounded-full font-medium hover:scale-105 hover:shadow-lg transition-all duration-300"
        >
          View All Projects
        </Link>
      </div>

      <div className="border-t border-black/20 dark:border-white/20 flex flex-col w-full">
        {items.map((item) => (
          <ProjectAccordionItem key={item.id} item={item} />
        ))}
      </div>
    </div>
  );
};
